import * as yup from 'yup';
import errorMessages from "protocol-setup/constants/errorMessages";

const { raveSetupForm } = errorMessages.openRave;

const METHODS_REQUIRING_X_AND_Y = [
  "First X by Arm, then every Y by Arm",
  "First X then every Y patient"
];

const requiresPatientSelection = (isSdpSupported, cmPatientSelectionMethod) => {
  if (isSdpSupported !== true) return false;
  return cmPatientSelectionMethod &&
    METHODS_REQUIRING_X_AND_Y.includes(cmPatientSelectionMethod);
};

const patientNumberSchema = (label) => yup
  .number(raveSetupForm.patientNumberSchema.typeError)
  .typeError(raveSetupForm.patientNumberSchema.typeError)
  .nullable()
  .transform((value, originalValue) => originalValue === "" ? null : value)
  .max(9999, raveSetupForm.patientNumberSchema.max)
  .when(['isSdpSupported', 'cmPatientSelectionMethod'], ([isSdpSupported, cmPatientSelectionMethod], schema) => {
    return requiresPatientSelection(isSdpSupported, cmPatientSelectionMethod)
      ? schema.required(raveSetupForm.patientNumberSchema.required(label))
      : schema.notRequired().strip();
  });

const raveSetupFormSchema = yup.object({
  isRaveProtocol: yup
    .boolean(raveSetupForm.isRaveProtocol.required)
    .required(raveSetupForm.isRaveProtocol.required),

  isUatStudyNameAsProd: yup
    .boolean(raveSetupForm.isUatStudyNameAsProd.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isUatStudyNameAsProd.required)
        : schema.notRequired();
    }),

  uatRaveStudyName: yup
    .string()
    .max(100, raveSetupForm.uatRaveStudyName.max)
    .when('isUatStudyNameAsProd', ([isUatStudyNameAsProd], schema) => {
      return isUatStudyNameAsProd === false
        ? schema.required(raveSetupForm.uatRaveStudyName.required)
        : schema.notRequired();
    }),

  openRaveAlsVersion: yup
    .string()
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.openRaveAlsVersion.required)
        : schema.notRequired();
    }),

  isRaveCtepAersInt: yup
    .boolean(raveSetupForm.isRaveCtepAersInt.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isRaveCtepAersInt.required)
        : schema.notRequired();
    }),

  ctepAersStepNumber: yup
    .number()
    .typeError(raveSetupForm.ctepAersStepNumber.typeError)
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .min(1, raveSetupForm.ctepAersStepNumber.min)
    .max(99, raveSetupForm.ctepAersStepNumber.max)
    .when('isRaveCtepAersInt', ([isRaveCtepAersInt], schema) => {
      return isRaveCtepAersInt === true
        ? schema.required(raveSetupForm.ctepAersStepNumber.required)
        : schema.notRequired();
    }),

  isTsdvSiteAuditing: yup
    .boolean(raveSetupForm.isTsdvSiteAuditing.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isTsdvSiteAuditing.required)
        : schema.notRequired();
    }),

  isDqpSupported: yup
    .boolean(raveSetupForm.isDqpSupported.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isDqpSupported.required)
        : schema.notRequired();
    }),

  isStudyRaveCalendaring: yup
    .boolean(raveSetupForm.isStudyRaveCalendaring.required)
    .when('isDqpSupported', ([isDqpSupported], schema) => {
      return isDqpSupported === true
        ? schema.required(raveSetupForm.isStudyRaveCalendaring.required)
        : schema.notRequired();
    }),

  isSdpSupported: yup
    .boolean(raveSetupForm.isSdpSupported.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isSdpSupported.required)
        : schema.notRequired();
    }),

  cmEffectiveCheckBox: yup
    .boolean()
    .notRequired(),

  cmEffectiveDt: yup
    .string()
    .when(['isSdpSupported', 'cmEffectiveCheckBox'], ([isSdpSupported, cmEffectiveCheckBox], schema) => {
      return isSdpSupported === true && cmEffectiveCheckBox === false
        ? schema.required(raveSetupForm.cmEffectiveDt.required)
        : schema.notRequired();
    }),

  cmStepNumber: yup
    .number()
    .typeError(raveSetupForm.cmStepNumber.typeError)
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .min(1, raveSetupForm.cmStepNumber.min)
    .max(99, raveSetupForm.cmStepNumber.max)
    .when('isSdpSupported', ([isSdpSupported], schema) => {
      return isSdpSupported === true
        ? schema.required(raveSetupForm.cmStepNumber.required)
        : schema.notRequired();
    }),

  cmPatientSelectionMethod: yup
    .string()
    .when('isSdpSupported', ([isSdpSupported], schema) => {
      return isSdpSupported === true
        ? schema.required(raveSetupForm.cmPatientSelectionMethod.required)
        : schema.notRequired();
    }),

  cmFirstXPatients: patientNumberSchema('Patient 1st X'),
  cmNextYPatients: patientNumberSchema('Patient next Y'),

  isMedidataPatientCloudEpro: yup
    .boolean(raveSetupForm.isMedidataPatientCloudEpro.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isMedidataPatientCloudEpro.required)
        : schema.notRequired();
    }),

  isRequiredTriadTesting: yup
    .boolean(raveSetupForm.isRequiredTriadTesting.required)
    .when('isRaveProtocol', ([isRaveProtocol], schema) => {
      return isRaveProtocol === true
        ? schema.required(raveSetupForm.isRequiredTriadTesting.required)
        : schema.notRequired();
    })
});

export default raveSetupFormSchema;
